/**
 * Host routing.
 *
 * One SPA serves three audiences (`docs/architecture/15-platform-foundations.md` §1):
 *   - `{slug}.{TENANT_BASE_DOMAIN}` → a bank (tenant)
 *   - `/platform/*` on the main SPA host → the platform console
 *   - anything else (localhost, an IP, a custom domain) → treated as a bank
 *
 * The platform console must be *unreachable* on a bank host, not merely hidden,
 * so this is the single place that decides, and the route tree is built from it.
 */

/** Retained for backwards-compatible admin-host deployments. */
export const PLATFORM_SUBDOMAIN = 'admin'

/** Hosts that never carry a tenant slug. */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1'])

export interface HostInfo {
  /** The full hostname, lower-cased, without the port. */
  host: string
  /** The left-most label, or null for a bare/unknown host. */
  subdomain: string | null
  /** True when this host serves the platform console. */
  isPlatform: boolean
}

/** Parse a hostname into its routing parts. Accepts an optional port. */
export function parseHost(hostname: string, baseDomain: string = ''): HostInfo {
  const host = hostname.trim().toLowerCase().replace(/:\d+$/, '')
  const labels = host.split('.').filter(Boolean)
  const base = baseDomain.trim().toLowerCase().replace(/^\./, '')

  let subdomain: string | null = null
  if (labels.length > 1) {
    // `demo.localhost` → `demo`; `a.b.example.com` with base `example.com` → `a.b`.
    const suffixLength = base ? base.split('.').length : 1
    const prefix = labels.slice(0, Math.max(0, labels.length - suffixLength))
    subdomain = prefix.length > 0 ? prefix.join('.') : null
  }

  return {
    host,
    subdomain,
    // The left-most label is the routing label. Checking the first label (rather than the whole
    // computed subdomain) keeps `admin.example.com` working when the base domain is not known at
    // build time — the SPA is a single artifact and gets no base-domain config.
    isPlatform: labels[0] === PLATFORM_SUBDOMAIN,
  }
}

/** Host routing for the current document, falling back to a bank host. */
export function currentHostInfo(baseDomain: string = ''): HostInfo {
  if (typeof window === 'undefined') return { host: '', subdomain: null, isPlatform: false }
  const info = parseHost(window.location.hostname, baseDomain)
  // A bare localhost (no subdomain) is the ordinary bank app, not the console.
  if (LOCAL_HOSTS.has(info.host)) return { ...info, subdomain: null, isPlatform: false }
  return info
}

/** True when the SPA is running on the platform (admin) host. */
export function isPlatformHost(baseDomain: string = ''): boolean {
  if (currentHostInfo(baseDomain).isPlatform) return true
  if (typeof window === 'undefined') return false

  // The platform console shares the main frontend origin. Bank routes remain
  // available at /login and /, while /platform/* selects the platform route tree.
  return window.location.pathname === '/platform' || window.location.pathname.startsWith('/platform/')
}
